import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import { Button, Input, Select, TextArea, Tooltip } from '@components/common';

import { projectUsersFormatted } from '@utils/formatter';

import { TNameTitleValueLabel } from '@interfaces/common/common';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { IProjectFormValues } from '@interfaces/components/form';
import { ICheckProjectCodeDTO, IProjectDTO } from '@interfaces/portal/dto/projectManagementDTO';

import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { regExpNumbersAndLetters } from '@constants/regExp';
import { MAX_10_LENGTH, MAX_80_LENGTH, MAX_1000_LENGTH } from '@constants/validationMessages';

import { getSchema } from './schema';
import WorkflowSelectOption from './WorkflowSelectOption';

import styles from './ProjectForm.module.scss';

const SELECT_OPTION_MAX_WIDTH = 180;

interface IProjectFormProps {
	isEditMode: boolean;
	toggleCloseConfirmModal: () => void;
	organisationId: string;
	submitForm: (values: IProjectFormValues) => void;
	editableProject: IProjectDTO | null;
	setIsDirtyFormModal: React.Dispatch<React.SetStateAction<boolean>>;
	handleCheckProjectCode: (data: ICheckProjectCodeDTO) => void;
	projectsStatuses: TSelectOption[];
	projectsWorkflows: TSelectOption[];
	projectAdmins: TSelectOption[];
	projectMembers: TSelectOption[];
}

const ProjectForm = ({
	isEditMode,
	toggleCloseConfirmModal,
	organisationId,
	submitForm,
	editableProject,
	setIsDirtyFormModal,
	handleCheckProjectCode,
	projectsStatuses,
	projectsWorkflows,
	projectMembers,
	projectAdmins,
}: IProjectFormProps) => {
	const [isWorkflowMenuOpen, setIsWorkflowMenuOpen] = useState(false);

	const onWorkflowMenuOpen = () => setIsWorkflowMenuOpen(true);
	const onWorkflowMenuClose = () => setIsWorkflowMenuOpen(false);

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		watch,
		control,
	} = useForm<IProjectFormValues>({
		resolver: yupResolver(getSchema({
			handleCheckProjectCode,
			organizationId: organisationId,
			isEditMode,
		})),
		mode: EValidationMode.onSubmit,
		reValidateMode: ERevalidationMode.onChange,
		defaultValues: {
			projectName: editableProject?.name || '',
			projectCode: editableProject?.code || '',
			description: editableProject?.description || '',
			status: editableProject?.status && {
				...editableProject.status,
				value: editableProject.status.name,
				label: editableProject.status.title,
			},
			workflow: editableProject?.workflow && {
				...editableProject.workflow,
				value: editableProject.workflow.name,
				label: `${editableProject.workflow.title} (${editableProject.workflow.statuses})`,
			},
			admins: projectUsersFormatted(editableProject?.admins),
			members: projectUsersFormatted(editableProject?.members),
		},
	});

	const onChangeAdmins = (options: TNameTitleValueLabel[]) => setValue('admins', options);

	const onChangeMembers = (options: TNameTitleValueLabel[]) => setValue('members', options);

	const adminsValue = watch('admins');
	const membersValue = watch('members');
	const workflow = watch('workflow');

	const onSubmit = (values: IProjectFormValues) => submitForm(values);

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	const descriptionLength = watch('description')?.trim().length || 0;

	return (
		<div className={styles.projectFormContainer}>
			<div className={styles.projectName}>
				<Input
					errorMessage={errors?.projectName?.message}
					label='Project Name'
					maxLength={MAX_80_LENGTH}
					name='projectName'
					placeholder='Enter name'
					register={register}
					required
					setValue={setValue}
				/>
			</div>
			<div className={styles.projectCode}>
				<Input
					disabled={isEditMode}
					errorMessage={errors?.projectCode?.message}
					label='Project Code'
					maxLength={MAX_10_LENGTH}
					name='projectCode'
					placeholder='Enter code'
					regexValidation={regExpNumbersAndLetters}
					register={register}
					required
					setValue={setValue}
				/>
			</div>
			<div className={styles.status}>
				<Select
					control={control}
					errorMessage={errors.status?.message}
					isError={!!errors?.status}
					label='Status'
					name='status'
					options={projectsStatuses}
					placeholder='Choose status'
					required
				/>
			</div>
			<Tooltip
				content={`${workflow?.label}`}
				isDisabled={!workflow?.label || isWorkflowMenuOpen}
				offset={[0, -10]}
				placement='bottom'
			>
				<div className={styles.workflow}>
					<Select
						control={control}
						customComponents={{
							Option: (props) => WorkflowSelectOption({
								...props,
							}),
						}}
						disabled={isEditMode}
						errorMessage={errors.workflow?.message}
						isError={!!errors?.workflow}
						label='Workflow'
						name='workflow'
						onMenuClose={onWorkflowMenuClose}
						onMenuOpen={onWorkflowMenuOpen}
						options={projectsWorkflows}
						placeholder='Choose workflow'
						required
					/>
				</div>
			</Tooltip>
			<div className={styles.projectDescription}>
				<TextArea
					errorMessage={errors.description?.message}
					isMaxLengthCounter
					isNotResizable
					label='Project Description'
					maxLength={MAX_1000_LENGTH}
					name='description'
					placeholder='Enter description'
					register={register}
					valueLength={descriptionLength}
					withUseForms
				/>
			</div>
			<div className={styles.admins}>
				<Select
					closeMenuOnSelect={false}
					control={control}
					handleChange={onChangeAdmins}
					isClearable={false}
					isError={!!errors.admins}
					isMulti
					label='Project Admins'
					multiselectText='admins selected'
					name='admins'
					optionMaxWidth={SELECT_OPTION_MAX_WIDTH}
					options={projectAdmins}
					placeholder='Choose admins'
					value={adminsValue}
				/>
			</div>
			<div className={styles.members}>
				<Select
					closeMenuOnSelect={false}
					control={control}
					handleChange={onChangeMembers}
					isClearable={false}
					isError={!!errors.members}
					isMulti
					label='Project Members'
					multiselectText='members selected'
					name='members'
					optionMaxWidth={SELECT_OPTION_MAX_WIDTH}
					options={projectMembers}
					placeholder='Choose members'
					value={membersValue}
				/>
			</div>
			<div className={styles.submitButtons}>
				<Button
					onClick={toggleCloseConfirmModal}
					text='Cancel'
					withoutBackground
				/>
				<Button
					onClick={handleSubmit(onSubmit)}
					text='Save'
				/>
			</div>
		</div>
	);
};

export default ProjectForm;
